import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Input,
  Row,
  Button,
  FormFeedback,
  Alert,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";

// Import your assets - adjust path as needed

import imageLogo from "../../../assets/orderingSystem/login-bg.jpg";
import { useDispatch, useSelector } from "react-redux";

import { useNavigate, useParams, Link } from "react-router-dom";
import { verifyOtp } from "../redux/slices/customerAuthSlice";
import PublicOrderingServices from "../services/PublicOrderingServices";
import { clearCart } from "../../../slices/cart/publicOrderingCartSlice";
const arrow =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50'%3E%3Cpath d='M10 25 Q50 5 80 25' stroke='white' fill='none' stroke-width='3' stroke-linecap='round'/%3E%3Cpolygon points='80,25 70,15 75,25 70,35' fill='white'/%3E%3C/svg%3E";

const VerifyOtp = () => {
  const [errorMsg, setErrorMsg] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { emailForOtp, phoneForOtp, isPhoneRegistration, isPhoneLogin, loading, error, pendingReset, isEmailLoginOtp } = useSelector(
    (s) => s.customerAuth || {}
  );
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const { orgcode } = useParams();
  console.log("🚀 ~ VerifyOtp ~ orgcode:", orgcode);

  // Determine which identifier is being used
  const verificationIdentifier = isPhoneRegistration || isPhoneLogin ? phoneForOtp : emailForOtp;
  const verificationType = isPhoneRegistration ? "phone-registration" : isPhoneLogin ? "phone-login" : isEmailLoginOtp ? "email-login" : "email";

  // if neither emailForOtp nor phoneForOtp is present, user should go back to register
  useEffect(() => {
    if (!verificationIdentifier) {
      // small delay to allow UX; navigate back to register
      const t = setTimeout(
        () => navigate(`/${orgcode}/customer-register`),
        800
      );
      return () => clearTimeout(t);
    }
  }, [verificationIdentifier, navigate, orgcode]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .length(6, "OTP must be 6 digits")
        .matches(/^[0-9]{6}$/, "OTP must contain only numbers")
        .required("OTP is required"),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMsg(null);
        // If this OTP was requested as part of forgot-password flow,
        // navigate to reset-password page and pass the entered otp.
        if (pendingReset) {
          navigate(`/${orgcode}/reset-password`, { state: { otp: values.otp } });
          return;
        }

        const res = await dispatch(
          verifyOtp({ 
            email: verificationIdentifier, 
            otp: values.otp,
            isPhoneNumber: isPhoneRegistration || isPhoneLogin
          })
        );
        if (res?.type?.includes("fulfilled")) {
          // Handle different verification types
          if (isPhoneLogin) {
            // For phone login, place the order directly if pending
            try {
              const pendingRaw = localStorage.getItem("pendingCheckout");
              const pending = pendingRaw ? JSON.parse(pendingRaw) : null;

              if (pending && pending.orderPayload) {
                const response = await PublicOrderingServices.orderOnline({
                  token: res.payload?.token || res.payload?.accessToken,
                  apikey: orgcode,
                  orderPayload: pending.orderPayload,
                  phoneNumber: phoneForOtp
                });

                // Clear cart and pending checkout
                try {
                  localStorage.removeItem("publicCartV2");
                } catch (e) {}
                dispatch(clearCart());
                localStorage.removeItem("pendingCheckout");

                // Navigate to order summary
                navigate(`/${orgcode}/order-summary`, {
                  state: { 
                    orgData: pending.orgData, 
                    orderPost: response, 
                    orderResponse: response,
                    phoneNumber: phoneForOtp
                  },
                });
                return;
              } else {
                // No pending order, navigate to home
                navigate(`/${orgcode}`);
                return;
              }
            } catch (err) {
              console.error("Error placing order after phone login:", err);
              setErrorMsg("Order placement failed. Please try again.");
              return;
            }
          } else {
            // For email registration or phone registration, navigate to login
            navigate(`/${orgcode}/customer-login`);
          }
        } else {
          const errorMsg =
            res?.payload?.message ||
            res?.payload ||
            "OTP verification failed. Please try again.";
          setErrorMsg(errorMsg);
        }
      } catch (err) {
        setErrorMsg("OTP verification failed. Please try again.");
        console.error("OTP verification failed:", err);
      }
    },
  });

  const handleResendOtp = async () => {
    if (canResend && !resendLoading) {
      try {
        setResendLoading(true);
        setResendTimer(60);
        setCanResend(false);
        setErrorMsg(null);

        let res;
        if (isPhoneLogin || isPhoneRegistration) {
          // Resend OTP for phone
          res = await dispatch(customerPhoneLogin(phoneForOtp));
        } else if (isEmailLoginOtp) {
          // Resend OTP for email
          res = await dispatch(emailOtpLogin(emailForOtp));
        } else {
          // For other cases (regular email verification), use forgot password
          res = await dispatch(emailOtpLogin(emailForOtp));
        }

        if (res?.type?.includes("fulfilled")) {
          // OTP resent successfully
          console.log("OTP resent successfully");
        } else {
          const errorMsg = res?.payload?.message || res?.payload || "Failed to resend OTP";
          setErrorMsg(errorMsg);
          // Reset timer if failed
          setCanResend(true);
          setResendTimer(0);
        }
      } catch (err) {
        console.error("Error resending OTP:", err);
        setErrorMsg("Failed to resend OTP. Please try again.");
        // Reset timer if failed
        setCanResend(true);
        setResendTimer(0);
      } finally {
        setResendLoading(false);
      }
    }
  };

  useEffect(() => {
    if (errorMsg) {
      const timer = setTimeout(() => {
        setErrorMsg(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMsg]);

  document.title = "Ordering System";

  return (
    <React.Fragment>
      <div className="vh-100">
        <Row className="h-100 g-0">
          {/* OTP Form - Large screens */}
          <Col
            lg={{ size: 5, order: 2 }}
            md={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-none d-sm-none d-md-block d-lg-block"
            style={{ backgroundColor: "#0f403b" }}
          >
            <div className="py-5 d-flex flex-column justify-content-center align-items-center h-100">
              <CardBody
                className="p-4"
                style={{ maxWidth: "450px", width: "100%" }}
              >
                <div className="text-center mb-4">
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      margin: "0 auto 20px",
                      backgroundColor: "#28a745",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={isPhoneRegistration || isPhoneLogin ? "ri-phone-line" : "ri-mail-check-line"}
                      style={{ fontSize: "40px", color: "white" }}
                    ></i>
                  </div>
                  <h5 className="text-white mb-2">
                    {isPhoneRegistration 
                      ? "Verify Your Phone" 
                      : isPhoneLogin 
                        ? "Verify Your Phone for Login" 
                        : isEmailLoginOtp
                          ? "Verify Your Email for Login"
                          : "Verify Your Email"}
                  </h5>
                  <p className="text-white-50">
                    We've sent a verification code to:
                  </p>
                  <p
                    className="text-success fw-bold"
                    style={{ fontSize: "14px" }}
                  >
                    {verificationIdentifier}
                  </p>
                </div>

                {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                <div className="p-2 py-4">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    {/* OTP Input */}
                    <div className="mb-4">
                      <label className="text-white mb-2 d-block">
                        Enter OTP Code
                      </label>
                      <Input
                        name="otp"
                        className="form-control p-3 text-center"
                        placeholder="000000"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          validation.setFieldValue("otp", value);
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        invalid={
                          validation.touched.otp && validation.errors.otp
                            ? true
                            : false
                        }
                        style={{
                          fontSize: "24px",
                          letterSpacing: "8px",
                          fontWeight: "600",
                          textAlign: "center",
                        }}
                      />
                      {validation.touched.otp && validation.errors.otp ? (
                        <FormFeedback
                          type="invalid"
                          style={{ display: "block" }}
                        >
                          {validation.errors.otp}
                        </FormFeedback>
                      ) : null}
                    </div>

                    {/* Verify Button */}
                    <div className="mb-3">
                      <Button
                        disabled={loading || !validation.values.otp}
                        color="success"
                        className="btn btn-success w-100"
                        type="submit"
                        onClick={() => validation.handleSubmit()}
                        style={{ padding: "10px" }}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Verifying...
                          </>
                        ) : (
                          "Verify OTP"
                        )}
                      </Button>
                    </div>

                    {/* Resend OTP */}
                    <div className="text-center">
                      <p className="text-white-50 mb-2">
                        Didn't receive the code?
                      </p>
                      <Button
                        disabled={!canResend || loading}
                        color="link"
                        className="text-warning p-0"
                        type="button"
                        onClick={handleResendOtp}
                        style={{ textDecoration: "none" }}
                      >
                        {canResend ? (
                          "Resend OTP"
                        ) : (
                          <span>
                            Resend in <strong>{resendTimer}s</strong>
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </CardBody>

              <footer className="mt-auto w-100">
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center">
                        <p className="mb-0 text-muted">
                          &copy; {new Date().getFullYear()} Danfe Solution
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </footer>
            </div>
          </Col>

          {/* OTP Form - Small screens */}
          <Col
            lg={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-block d-sm-block d-md-none d-lg-none position-relative"
          >
            <img
              src={imageLogo}
              alt="Ordering System"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
            <div
              className="py-5 d-flex flex-column justify-content-center align-items-center h-100"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                overflowY: "auto",
              }}
            >
              <CardBody className="p-2 d-block justify-content-center align-content-center align-items-center w-100">
                <div className="text-center">
                  <h3
                    style={{ color: "white" }}
                    className="fw-bold mb-4 d-lg-none d-block"
                  >
                    Ordering System
                    <img src={arrow} alt="" className="w-25 ms-2" />
                  </h3>
                  <div
                    style={{
                      width: "70px",
                      height: "70px",
                      margin: "0 auto 20px",
                      backgroundColor: "#28a745",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i
                      className={isPhoneRegistration || isPhoneLogin ? "ri-phone-line" : "ri-mail-check-line"}
                      style={{ fontSize: "35px", color: "white" }}
                    ></i>
                  </div>
                  <h5 className="text-white mb-2">
                    {isPhoneRegistration 
                      ? "Verify Your Phone" 
                      : isPhoneLogin 
                        ? "Verify Your Phone for Login" 
                        : isEmailLoginOtp
                          ? "Verify Your Email for Login"
                          : "Verify Your Email"}
                  </h5>
                  <h6 className="text-light">
                    We've sent a verification code to:
                  </h6>
                  <p
                    className="text-success fw-bold"
                    style={{ fontSize: "13px" }}
                  >
                    {verificationIdentifier}
                  </p>
                </div>

                {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

                <div className="p-2 py-3">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    {/* OTP Input */}
                    <div className="mb-4">
                      <label className="text-white mb-2 d-block">
                        Enter OTP Code
                      </label>
                      <Input
                        name="otp"
                        className="form-control p-3 text-center"
                        placeholder="000000"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          validation.setFieldValue("otp", value);
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        invalid={
                          validation.touched.otp && validation.errors.otp
                            ? true
                            : false
                        }
                        style={{
                          fontSize: "24px",
                          letterSpacing: "8px",
                          fontWeight: "600",
                          textAlign: "center",
                        }}
                      />
                      {validation.touched.otp && validation.errors.otp ? (
                        <FormFeedback
                          type="invalid"
                          style={{ display: "block" }}
                        >
                          {validation.errors.otp}
                        </FormFeedback>
                      ) : null}
                    </div>

                    {/* Verify Button */}
                    <div className="mb-3">
                      <Button
                        disabled={loading || !validation.values.otp}
                        color="success"
                        className="btn btn-success w-100"
                        type="submit"
                        onClick={() => validation.handleSubmit()}
                        style={{ padding: "10px" }}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Verifying...
                          </>
                        ) : (
                          "Verify OTP"
                        )}
                      </Button>
                    </div>

                    {/* Resend OTP */}
                    <div className="text-center">
                      <p className="text-white-50 mb-2">
                        Didn't receive the code?
                      </p>
                      <Button
                        disabled={!canResend || loading}
                        color="link"
                        className="text-warning p-0"
                        type="button"
                        onClick={handleResendOtp}
                        style={{ textDecoration: "none" }}
                      >
                        {canResend ? (
                          "Resend OTP"
                        ) : (
                          <span>
                            Resend in <strong>{resendTimer}s</strong>
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </CardBody>

              <footer className="mt-auto w-100">
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center">
                        <p className="mb-0 text-muted">
                          &copy; {new Date().getFullYear()} Danfe Solution
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </footer>
            </div>
          </Col>

          {/* Image Part - Desktop Only */}
          <Col
            lg={{ size: 7, order: 1 }}
            md={{ size: 7, order: 1 }}
            xl={{ size: 6, order: 1 }}
            xs={{ size: 12, order: 2 }}
            sm={{ size: 12, order: 2 }}
            className="position-relative h-100 d-none d-sm-none d-md-flex d-lg-flex"
          >
            {/* Background Image */}
            <img
              src={imageLogo}
              alt="Background"
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
            />

            {/* Overlay */}
            <div className="login-overlay d-flex flex-column justify-content-center align-items-start position-absolute w-100 h-100">
              <div className="overlay-content w-100 px-4">
                <h1 className="fw-bold display-5 mb-4 text-white d-flex align-items-center">
                  Public Ordering System
                  <img
                    src={arrow}
                    alt=""
                    className="ms-2"
                    style={{ height: "40px" }}
                  />
                </h1>
                <div>
                  <Card
                    className="p-4 border-0"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                  >
                    <div className="text-center mb-4">
                      <div
                        style={{
                          width: "100px",
                          height: "100px",
                          margin: "0 auto 20px",
                          backgroundColor: "#28a745",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i
                          className="ri-shield-check-line"
                          style={{ fontSize: "50px", color: "white" }}
                        ></i>
                      </div>
                    </div>
                    <h5 className="text-white text-center mb-3">
                      Secure Verification
                    </h5>
                    <ul className="list-unstyled text-white">
                      <li className="mb-2">
                        <i className="ri-check-line text-success me-2"></i>
                        Fast and secure verification
                      </li>
                      <li className="mb-2">
                        <i className="ri-check-line text-success me-2"></i>
                        One-time password (OTP)
                      </li>
                      <li className="mb-2">
                        <i className="ri-check-line text-success me-2"></i>
                        Protects your account
                      </li>
                      <li className="mb-2">
                        <i className="ri-check-line text-success me-2"></i>
                        Instant verification
                      </li>
                      <li className="mb-2">
                        <i className="ri-check-line text-success me-2"></i>
                        No registration fees
                      </li>
                    </ul>
                  </Card>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        .login-overlay {
          background: rgba(15, 64, 59, 0.7);
          top: 0;
          left: 0;
        }

        .form-control {
          border-radius: 8px;
          border: 1px solid #ddd;
        }

        .form-control:focus {
          border-color: #28a745;
          box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25);
        }

        .btn-success {
          background-color: #28a745;
          border-color: #28a745;
          border-radius: 8px;
          font-weight: 600;
        }

        .btn-success:hover {
          background-color: #218838;
          border-color: #218838;
        }

        .btn-success:disabled {
          background-color: #ccc;
          border-color: #ccc;
          cursor: not-allowed;
        }

        .btn-link {
          color: #ffc107 !important;
          text-decoration: none;
        }

        .btn-link:disabled {
          color: #999 !important;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .vh-100 {
            min-height: 100vh;
          }
        }
      `}</style>
    </React.Fragment>
  );
};

export default VerifyOtp;
